import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ActionButton,
  Image,
  TextField,
  TooltipTrigger,
  Tooltip
} from '@adobe/react-spectrum';
import Edit from '@spectrum-icons/workflow/Edit';
import { useDispatch, useSelector } from 'react-redux';
import styles from './Header.module.scss';
import shareSvg from 'app/assets/figma/slideout/share.svg';
import closeSvg from 'app/assets/figma/slideout/close.svg';
import ChevronDropdownIcon from 'app/assets/figma/slideout/chevron-dropdown-old.svg';
import chevronDropdownSvg from 'app/assets/icons/chevron-dropdown.svg';
import ChevronLeft from 'app/assets/svgIcons/ChevronLeft';
import ChevronRight from 'app/assets/svgIcons/ChevronRight';
import CloneIcon from 'app/assets/svgIcons/CloneIcon';
import { ProductDropdown } from './ProductDropdown';
import { MultipleProductIcons } from './MultipleProductIcons';
import CheckmarkIcon from './CheckmarkIcon';
import { IS_STAGE_ENVIRONMENT } from '../../Shell';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { useRBAC } from '../context/RBACContext';
import FeatureHoverPopup from '../Features/FeatureHoverPopup';
import { ChangeMomentTypeDropdown } from './ChangeMomentTypeDropdown';
import { CloneMomentModal } from '../../CloneMomentModal/CloneMomentModal';
import { ExportPdfButton } from '../ExportPdfButton';
import { useSlideoutUrl } from '../../../hooks/useSlideoutUrl';
import { stripRtmPhaseNamePrefix } from 'utils/rtmPhaseDisplayTitle';
import { isEolMoment } from 'utils/constants';
import {
  getEolPhaseTitlePrefix,
  stripEolPhaseNamePrefix
} from 'utils/eolPhaseTitlePrefix';

interface ProductGroup {
  name?: string;
  label?: string;
}

interface HeaderProps {
  title: string;
  categories?: string[];
  onClose: () => void;
  productIcon?: React.ReactNode;
  isFullScreen?: boolean;
  onMaximize?: () => void;
  MaximizeIcon?: React.ComponentType<any>;
  issueId?: string; // For generating the share link
  onCopySuccess?: () => void; // Callback for successful copy
  onTitleUpdate?: (newTitle: string) => void; // Callback for title updates
  onNavigatePrevious?: () => void; // Callback for previous navigation
  onNavigateNext?: () => void; // Callback for next navigation
  // New product-related props
  products?: string[]; // Array of current products
  selectedProducts?: string[]; // Array of selected products for multi-select
  onProductSelectionChange?: (products: string[]) => void; // Callback for product selection changes
  availableProducts?: string[]; // Available products for selection
  enableProductDropdown?: boolean; // Whether to enable the dropdown functionality
  productGroups?: Array<string | ProductGroup>; // Product groups for reliable title extraction
  /** When set, stripped from the title string before edit/save (RTM phase is display-only). */
  rtmPhaseTitleLabel?: string;
  /** EOL release phase name — drives stage prefix (EOL:, EOSa:, …). */
  eolReleasePhase?: string;
  isCommentPanelOpen?: boolean; // Whether comment panel is open
  // New props for moment type, surfaces, GenAI, and Consumes Credit
  momentType?: string; // Moment type (read-only display)
  surfaces?: string[]; // Array of surfaces (Desktop, Web, Mobile, etc.)
  genai?: boolean; // GenAI flag from moment data
  consumesCredit?: boolean; // Consumes Credit flag from moment data
  onGenAiChange?: (value: boolean) => void; // Callback for GenAI changes
  onConsumesCreditChange?: (value: boolean) => void; // Callback for Consumes Credit changes
  onMomentTypeChange?: (newType: string) => void; // Callback for moment type changes
  onClone?: (clonedName: string) => void; // Callback for clone action
  /** When set, clone button delegates here (e.g. unsaved-changes guard in SlideOutPanel). */
  onCloneClick?: () => void;
  /** Opens the browser print dialog to export the slideout as PDF. */
  onExportPdf?: () => Promise<void> | void;
  /** Controlled clone modal open state (paired with onCloneModalOpenChange). */
  isCloneModalOpen?: boolean;
  onCloneModalOpenChange?: (open: boolean) => void;
  parId?: string; // PAR ID for Price Changes and Offers
  onParIdChange?: (value: string) => void; // Callback for PAR ID updates
  /** Full-width divider below header (e.g. slideout AI assistant mode). */
  showBottomBorder?: boolean;
  /**
   * Extra content rendered at the start of the right-side section, before
   * the Stage badge/Clone/Share/chevron/maximize controls - purely
   * additive, `undefined` by default, so existing callers (the Release
   * Moment slide-out) render exactly as before. Added for the Feedback
   * slide-out's own Milestone-name/Due-Date pills, which have no
   * equivalent field on this component otherwise.
   */
  headerRightExtra?: React.ReactNode;
  /**
   * Hides the GenAI/Consumes-Credit pills' dropdown chevron - `false` by
   * default, so existing callers (the Release Moment slide-out) render
   * exactly as before. Those two pills' chevron is otherwise always shown
   * once `genai`/`consumesCredit` is defined, regardless of edit
   * permission (only the click-to-open behavior is permission-gated) -
   * this covers the case where a caller wants the pills fully
   * display-only, chevron included, for a viewer who can never edit
   * (e.g. the Feedback slide-out).
   */
  readOnlyChips?: boolean;
  /** Opt-in stacked layout for narrow screens; the default is unchanged. */
  mobileLayout?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  title,
  categories,
  onClose,
  productIcon,
  isFullScreen,
  onMaximize,
  MaximizeIcon,
  issueId,
  onCopySuccess,
  onTitleUpdate,
  onNavigatePrevious,
  onNavigateNext,
  products = [],
  selectedProducts = [],
  onProductSelectionChange,
  availableProducts,
  enableProductDropdown = false,
  productGroups = [],
  rtmPhaseTitleLabel,
  eolReleasePhase,
  isCommentPanelOpen = false,
  momentType,
  surfaces = [],
  genai = undefined,
  consumesCredit = undefined,
  onGenAiChange,
  onConsumesCreditChange,
  onMomentTypeChange,
  onClone,
  onCloneClick,
  onExportPdf,
  isCloneModalOpen: isCloneModalOpenProp,
  onCloneModalOpenChange,
  parId,
  onParIdChange,
  showBottomBorder = false,
  headerRightExtra,
  readOnlyChips = false,
  mobileLayout = false
}) => {
  // RBAC: Get permissions from context
  const permissions = useRBAC();
  const canEditGeneralInfo = permissions?.canEditGeneralInfo ?? false;

  const dispatch = useDispatch();

  const isEol = isEolMoment(momentType);
  const eolPrefixLabel = getEolPhaseTitlePrefix(eolReleasePhase);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [titleError, setTitleError] = useState('');
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1400);
  const [isMediumScreen, setIsMediumScreen] = useState(
    window.innerWidth > 1400 && window.innerWidth <= 1800
  );
  const shouldSaveOnBlurRef = React.useRef(true); // ✅ Track if onBlur should save

  // State for GenAI and Consumes Credit (local state synced with props)
  const [localGenAi, setLocalGenAi] = useState(genai);
  const [localConsumesCredit, setLocalConsumesCredit] =
    useState(consumesCredit);

  // Dropdown visibility state
  const [genAiDropdownOpen, setGenAiDropdownOpen] = useState(false);
  const [consumesCreditDropdownOpen, setConsumesCreditDropdownOpen] =
    useState(false);

  // PAR ID popup state
  const [parIdPopupOpen, setParIdPopupOpen] = useState(false);
  const [parIdPopupPosition, setParIdPopupPosition] = useState({ x: 0, y: 0 });
  const parIdPillRef = React.useRef<HTMLDivElement>(null);

  // Clone modal state (internal when not controlled by parent)
  const [internalCloneModalOpen, setInternalCloneModalOpen] = useState(false);
  const isCloneModalControlled = onCloneModalOpenChange !== undefined;
  const isCloneModalOpen = isCloneModalControlled
    ? isCloneModalOpenProp ?? false
    : internalCloneModalOpen;
  const setCloneModalOpen = (open: boolean) => {
    if (isCloneModalControlled) {
      onCloneModalOpenChange!(open);
    } else {
      setInternalCloneModalOpen(open);
    }
  };

  // Slideout URL hook for opening cloned moment
  const { openSlideout } = useSlideoutUrl();

  // Tooltip state management - to prevent instant tooltips
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Tooltip handlers
  const handleTooltipMouseEnter = (tooltipId: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(tooltipId);
    }, 1000); // 1 second delay
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  // Sync local state with props when they change
  React.useEffect(() => {
    setLocalGenAi(genai);
  }, [genai]);

  React.useEffect(() => {
    setLocalConsumesCredit(consumesCredit);
  }, [consumesCredit]);

  // Close dropdowns on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest('[data-dropdown="genai"]') &&
        !target.closest('[data-dropdown="consumes-credit"]')
      ) {
        setGenAiDropdownOpen(false);
        setConsumesCreditDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle GenAI change
  const handleGenAiChange = (value: boolean) => {
    setLocalGenAi(value);

    // If GenAI is set to No (false), automatically set Consumes Credit to No (false)
    if (!value) {
      setLocalConsumesCredit(false);
      if (onConsumesCreditChange) {
        onConsumesCreditChange(false);
      }
    }

    if (onGenAiChange) {
      onGenAiChange(value);
    }
  };

  // Handle Consumes Credit change
  const handleConsumesCreditChange = (value: boolean) => {
    setLocalConsumesCredit(value);
    if (onConsumesCreditChange) {
      onConsumesCreditChange(value);
    }
  };

  // Listen to window resize to detect screen size
  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsSmallScreen(width <= 1400);
      setIsMediumScreen(width > 1400 && width <= 1800);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = '/images/apps_icons/AdobeIcon.svg';
  };

  // Calculate dynamic width for product icons container
  const calculateProductIconsWidth = (productCount: number): string => {
    if (productCount === 0) {
      return '32px'; // Default single icon width
    } else if (productCount === 1) {
      return '32px'; // 1 icon = 32px
    } else if (productCount === 2) {
      return '74px'; // 2 icons = 32px + 10px + 32px = 74px
    } else if (productCount === 3) {
      return '116px'; // 3 icons = 32px + 10px + 32px + 10px + 32px = 106px + 10px gap before count = 116px
    } else {
      return '146px'; // 4+ icons = 32px + 10px + 32px + 10px + 32px + 10px + 20px (count badge) + 10px spacing = 136px
    }
  };

  // Extract only the title portion (remove product groups prefix, then RTM phase prefix)
  const extractTitleOnly = (fullTitle: string): string => {
    let t = fullTitle;
    if (productGroups && productGroups.length > 0) {
      const productGroupNames = productGroups.map(group => {
        return typeof group === 'string'
          ? group
          : group.name || group.label || String(group);
      });
      const productGroupsPrefix = productGroupNames.join(', ') + ' - ';
      if (t.startsWith(productGroupsPrefix)) {
        t = t.substring(productGroupsPrefix.length);
      }
    } else {
      const dashIndex = t.lastIndexOf(' - ');
      if (dashIndex !== -1) {
        t = t.substring(dashIndex + 3);
      }
    }
    if (rtmPhaseTitleLabel?.trim()) {
      t = stripRtmPhaseNamePrefix(t, rtmPhaseTitleLabel);
    }
    if (isEol) {
      t = stripEolPhaseNamePrefix(t);
    }
    return t;
  };

  /** Product-group / RTM prefix before `EOL:` (display-only; not part of the editable name). */
  const getTitlePrefixBeforeEol = (fullTitle: string): string => {
    const bareName = extractTitleOnly(fullTitle);
    if (!bareName) {
      return fullTitle.replace(/(?:(?:EOL|EOSa|EOSu|Decom):\s*)+$/i, '');
    }
    const nameIndex = fullTitle.lastIndexOf(bareName);
    if (nameIndex < 0) {
      return '';
    }
    return fullTitle
      .substring(0, nameIndex)
      .replace(/(?:(?:EOL|EOSa|EOSu|Decom):\s*)+$/i, '');
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
    setTitleError(''); // Clear any previous error
    // Extract only the title portion for editing (without product groups)
    const titleOnly = extractTitleOnly(title);
    setEditedTitle(titleOnly);
  };

  const handleTitleSave = () => {
    const trimmedTitle = editedTitle.trim();

    // Validate that title is not empty
    if (!trimmedTitle) {
      setTitleError('Title cannot be empty');
      return; // Don't save or exit edit mode
    }

    const currentTitleOnly = extractTitleOnly(title);
    if (onTitleUpdate && trimmedTitle !== currentTitleOnly) {
      onTitleUpdate(trimmedTitle);
    }

    // Clear error and exit edit mode only if validation passes
    setTitleError('');
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    const titleOnly = extractTitleOnly(title);
    setEditedTitle(titleOnly);
    setTitleError(''); // Clear any error when cancelling
    setIsEditingTitle(false);
  };

  const handleTitleChange = (value: string) => {
    setEditedTitle(value);
    // Clear error when user starts typing
    if (titleError && value.trim()) {
      setTitleError('');
    }
  };

  const handleTitleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleTitleSave();
    } else if (event.key === 'Escape') {
      handleTitleCancel();
    }
  };

  // ✅ New handler for blur event that checks the flag
  const handleTitleBlur = () => {
    if (shouldSaveOnBlurRef.current) {
      handleTitleSave();
    }
  };

  const handleShareClick = useCallback(async () => {
    if (!issueId) return;

    try {
      // Generate the RSD/issue link with correct moments path
      const currentUrl = window.location.origin;
      const shareLink = `${currentUrl}/moments/${issueId}`;

      // Copy to clipboard
      await navigator.clipboard.writeText(shareLink);

      // Call the parent callback for success feedback
      if (onCopySuccess) {
        onCopySuccess();
      }
    } catch (error) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = `${window.location.origin}/moments/${issueId}`;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        // Call the parent callback for success feedback
        if (onCopySuccess) {
          onCopySuccess();
        }
      } catch (fallbackError) {}
    }
  }, [issueId, onCopySuccess]);

  // Handle clone icon click
  const handleCloneClick = () => {
    if (onCloneClick) {
      onCloneClick();
      return;
    }
    setCloneModalOpen(true);
  };

  // Handle clone action
  const handleClone = (clonedName: string) => {
    console.log('Cloning moment with name:', clonedName);
    if (onClone) {
      onClone(clonedName);
    }
  };

  // Handle successful clone - open slideout with cloned moment
  const handleCloneSuccess = (clonedIssueId: string) => {
    console.log('🚀 Opening slideout for cloned moment:', clonedIssueId);
    setCloneModalOpen(false);
    // Open the slideout with the newly cloned moment
    openSlideout(clonedIssueId);
  };

  return (
    <View
      UNSAFE_className={[
        styles.header,
        showBottomBorder ? styles.headerWithBottomBorder : '',
        mobileLayout ? styles.headerMobile : ''
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="slide-out-header"
    >
      <div
        className={styles.headerContent}
        data-testid="slide-out-header-content"
      >
        <ActionButton
          UNSAFE_className={styles.closeButton}
          onPress={onClose}
          data-testid="slide-out-header-close-button"
        >
          <img
            src={closeSvg}
            alt="Close"
            width={20}
            height={20}
            data-testid="slide-out-header-close-icon"
          />
        </ActionButton>

        <span
          className={styles.separator}
          data-testid="slide-out-header-separator"
        ></span>

        {/* Title Container - Combined container for product icons, title and pills as per Figma */}
        <div
          className={styles.titleContainer}
          data-testid="slide-out-header-title"
        >
          <div className={styles.titleRowWrapper}>
            {/* Product Icons Container - Always visible, outside scrollable area */}
            <div
              className={styles.productIconsContainer}
              style={{
                width: enableProductDropdown
                  ? calculateProductIconsWidth(selectedProducts.length)
                  : calculateProductIconsWidth(products.length || 1)
              }}
            >
              {enableProductDropdown ? (
                <div
                  className={styles.productIcon}
                  data-testid="slide-out-header-product-icon"
                >
                  <ProductDropdown
                    selectedProducts={selectedProducts}
                    onSelectionChange={onProductSelectionChange || (() => {})}
                    availableProducts={availableProducts}
                    currentProductIcon={
                      <MultipleProductIcons
                        products={selectedProducts}
                        maxVisible={3}
                        iconSize="32px"
                      />
                    }
                  />
                </div>
              ) : (
                productIcon && (
                  <div
                    className={styles.productIcon}
                    data-testid="slide-out-header-product-icon"
                  >
                    {React.isValidElement(productIcon) &&
                      React.cloneElement(
                        productIcon as React.ReactElement<any>,
                        {
                          onError: handleImageError
                        }
                      )}
                  </div>
                )
              )}
            </div>

            {/* Title Wrapper - Only this scrolls */}
            <div className={styles.titleWrapper}>
              {/* Title Text or Editing Box */}
              {isEditingTitle ? (
                <div className={styles.editingBox}>
                  {isEol ? (
                    <div
                      className={[
                        styles.eolTitleInputGroup,
                        titleError ? styles.eolTitleInputGroupError : ''
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span
                        className={styles.eolTitleInputPrepend}
                        aria-hidden="true"
                      >
                        {eolPrefixLabel}
                      </span>
                      <TextField
                        value={editedTitle}
                        onChange={handleTitleChange}
                        onBlur={handleTitleBlur}
                        onKeyDown={handleTitleKeyDown}
                        isQuiet
                        UNSAFE_className={styles.eolTitleInputField}
                        autoFocus
                        aria-label="Edit title"
                      />
                    </div>
                  ) : (
                    <TextField
                      value={editedTitle}
                      onChange={handleTitleChange}
                      onBlur={handleTitleBlur}
                      onKeyDown={handleTitleKeyDown}
                      UNSAFE_className={styles.titleInput}
                      autoFocus
                      aria-label="Edit title"
                      validationState={titleError ? 'invalid' : undefined}
                      errorMessage={titleError}
                    />
                  )}
                  {isEol && titleError ? (
                    <div className={styles.titleErrorMessage}>{titleError}</div>
                  ) : null}
                </div>
              ) : (
                <>
                  {/* Title Text and Edit Button */}
                  <div
                    className={styles.clickableTitle}
                    onClick={canEditGeneralInfo ? handleTitleClick : undefined}
                    style={{
                      cursor: canEditGeneralInfo ? 'pointer' : 'default'
                    }}
                  >
                    {isEol ? (
                      <span className={styles.title}>
                        {getTitlePrefixBeforeEol(title)}
                        <span className={styles.eolTitlePrefixDisplay}>
                          {eolPrefixLabel}{' '}
                        </span>
                        {extractTitleOnly(title)}
                      </span>
                    ) : (
                      <Text UNSAFE_className={styles.title}>{title}</Text>
                    )}
                  </div>
                  {canEditGeneralInfo && (
                    <button
                      className={styles.editButton}
                      onClick={handleTitleClick}
                      aria-label="Edit title"
                      title="Edit title"
                    >
                      <Edit size="S" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Pills Section: Moment Type, Surfaces, GenAI, and Consumes Credit - Below Title */}
          {(momentType ||
            surfaces.length > 0 ||
            genai !== undefined ||
            consumesCredit !== undefined ||
            (mobileLayout && headerRightExtra)) && (
            <div className={styles.pillsContainer}>
              {mobileLayout && headerRightExtra}
              {/* Moment Type Pill/Dropdown - Appears first */}
              <ChangeMomentTypeDropdown
                momentType={momentType}
                canEdit={canEditGeneralInfo}
                onMomentTypeChange={onMomentTypeChange}
              />

              {/* Surfaces Pill (Read-only display) */}
              {surfaces.length > 0 && (
                <div
                  className={styles.pill}
                  style={{ cursor: 'default' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f8f8f8';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#f8f8f8';
                  }}
                >
                  <span className={styles.pillText}>{surfaces.join(', ')}</span>
                </div>
              )}

              {/* PAR ID Pill - editable for Price Changes and Offers */}
              {parId !== undefined && (
                <div
                  ref={parIdPillRef}
                  className={styles.pill}
                  role="button"
                  tabIndex={canEditGeneralInfo ? 0 : -1}
                  aria-label={`Edit PAR ID: ${parId || '—'}`}
                  aria-expanded={parIdPopupOpen}
                  style={{
                    cursor: canEditGeneralInfo ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (!canEditGeneralInfo) return;
                    if (parIdPopupOpen) {
                      setParIdPopupOpen(false);
                      return;
                    }
                    const rect = parIdPillRef.current?.getBoundingClientRect();
                    if (rect) {
                      const overlayRoot =
                        document.querySelector(
                          '[data-testid="slideout-panel"]'
                        ) || document.body;
                      const overlayRect = overlayRoot.getBoundingClientRect();
                      // Arrow center = container.left(28) + padding.left(28) + arrow.halfWidth(16) = 72px
                      const arrowTipOffsetFromPopupLeft = 72;
                      const pillCenterX =
                        rect.left - overlayRect.left + rect.width / 2;
                      setParIdPopupPosition({
                        x: Math.max(
                          0,
                          pillCenterX - arrowTipOffsetFromPopupLeft
                        ),
                        y: rect.bottom - overlayRect.top + 24
                      });
                    }
                    setParIdPopupOpen(true);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      (e.currentTarget as HTMLElement).click();
                    }
                  }}
                >
                  <span className={styles.pillText}>
                    PAR ID: {parId || '—'}
                  </span>
                  {canEditGeneralInfo && (
                    <img
                      src={chevronDropdownSvg}
                      width="8"
                      height="8"
                      className={styles.parIdChevron}
                      style={{ flexShrink: 0 }}
                      aria-hidden="true"
                      alt=""
                    />
                  )}
                </div>
              )}

              {parIdPopupOpen && (
                <FeatureHoverPopup
                  content={{
                    fieldType: 'parId',
                    parId: parId || '',
                    onSave: data => {
                      if (data.parId !== undefined && onParIdChange) {
                        onParIdChange(data.parId);
                      }
                    }
                  }}
                  isVisible={parIdPopupOpen}
                  position={parIdPopupPosition}
                  arrowDirection="down"
                  onRequestClose={() => {
                    setParIdPopupOpen(false);
                    // Return focus to the pill so keyboard users can continue navigating
                    setTimeout(() => parIdPillRef.current?.focus(), 0);
                  }}
                  triggerRef={parIdPillRef}
                />
              )}

              {/* GenAI Dropdown Pill */}
              {genai !== undefined && (
                <div style={{ position: 'relative' }} data-dropdown="genai">
                  <div
                    className={styles.pill}
                    style={{
                      cursor: readOnlyChips
                        ? 'default'
                        : canEditGeneralInfo
                        ? 'pointer'
                        : 'not-allowed'
                    }}
                    onClick={() => {
                      if (canEditGeneralInfo) {
                        setGenAiDropdownOpen(!genAiDropdownOpen);
                        setConsumesCreditDropdownOpen(false); // Close other dropdowns
                      }
                    }}
                  >
                    <span className={styles.pillText}>
                      GenAI: {localGenAi ? 'Yes' : 'No'}
                    </span>
                    {!readOnlyChips && (
                      <span className={styles.pillChevron} aria-hidden="true">
                        <img src={ChevronDropdownIcon} alt="" />
                      </span>
                    )}
                  </div>

                  {/* GenAI Dropdown Menu */}
                  {genAiDropdownOpen && canEditGeneralInfo && (
                    <div className={styles.dropdownMenu}>
                      <div
                        className={styles.dropdownItem}
                        onClick={() => {
                          handleGenAiChange(true);
                          setGenAiDropdownOpen(false);
                        }}
                      >
                        <span className={styles.dropdownItemText}>Yes</span>
                        {localGenAi && (
                          <BlackCheckMarkIcon width={18} height={14} />
                        )}
                      </div>
                      <div
                        className={styles.dropdownItem}
                        onClick={() => {
                          handleGenAiChange(false);
                          setGenAiDropdownOpen(false);
                        }}
                      >
                        <span className={styles.dropdownItemText}>No</span>
                        {!localGenAi && (
                          <BlackCheckMarkIcon width={18} height={14} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Consumes Credit Dropdown Pill */}
              {consumesCredit !== undefined && (
                <div
                  style={{ position: 'relative' }}
                  data-dropdown="consumes-credit"
                >
                  <div
                    className={styles.pill}
                    style={{
                      cursor: readOnlyChips
                        ? 'default'
                        : localGenAi && canEditGeneralInfo
                        ? 'pointer'
                        : 'not-allowed',
                      opacity: localGenAi ? 1 : 0.5 // Match Figma: 0.5 opacity when GenAI is false
                    }}
                    onClick={() => {
                      if (localGenAi && canEditGeneralInfo) {
                        setConsumesCreditDropdownOpen(
                          !consumesCreditDropdownOpen
                        );
                        setGenAiDropdownOpen(false); // Close other dropdowns
                      }
                    }}
                  >
                    <span className={styles.pillText}>
                      Consumes Credits: {localConsumesCredit ? 'Yes' : 'No'}
                    </span>
                    {!readOnlyChips && (
                      <span className={styles.pillChevron} aria-hidden="true">
                        <img src={ChevronDropdownIcon} alt="" />
                      </span>
                    )}
                  </div>

                  {/* Consumes Credit Dropdown Menu */}
                  {consumesCreditDropdownOpen &&
                    localGenAi &&
                    canEditGeneralInfo && (
                      <div className={styles.dropdownMenu}>
                        <div
                          className={styles.dropdownItem}
                          onClick={() => {
                            handleConsumesCreditChange(true);
                            setConsumesCreditDropdownOpen(false);
                          }}
                        >
                          <span className={styles.dropdownItemText}>Yes</span>
                          {localConsumesCredit && (
                            <BlackCheckMarkIcon width={18} height={14} />
                          )}
                        </div>
                        <div
                          className={styles.dropdownItem}
                          onClick={() => {
                            handleConsumesCreditChange(false);
                            setConsumesCreditDropdownOpen(false);
                          }}
                        >
                          <span className={styles.dropdownItemText}>No</span>
                          {!localConsumesCredit && (
                            <BlackCheckMarkIcon width={18} height={14} />
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Removed CreateButton as requested */}
      </div>

      <div
        className={styles.rightSection}
        data-testid="slide-out-header-right-section"
      >
        {!mobileLayout && headerRightExtra}

        {IS_STAGE_ENVIRONMENT && (
          <span className={styles.stageBadge}>Stage</span>
        )}

        {onExportPdf && (
          <ExportPdfButton variant="header" onExport={onExportPdf} />
        )}

        {/* Clone Icon */}
        {issueId && (
          <TooltipTrigger
            isOpen={openTooltip === 'clone'}
            onOpenChange={isOpen => {
              if (!isOpen) setOpenTooltip(null);
            }}
            placement="bottom"
          >
            <div
              onMouseEnter={() => handleTooltipMouseEnter('clone')}
              onMouseLeave={handleTooltipMouseLeave}
              style={{ display: 'contents' }}
            >
              <ActionButton
                onPress={handleCloneClick}
                aria-label="Clone moment"
                data-testid="slide-out-header-clone-button"
                UNSAFE_className={styles.shareButton}
                isQuiet
              >
                <CloneIcon width={16} height={16} />
              </ActionButton>
            </div>
            <Tooltip>Clone Moment</Tooltip>
          </TooltipTrigger>
        )}

        {/* Share Icon with copy functionality */}
        {issueId && (
          <TooltipTrigger
            isOpen={openTooltip === 'share'}
            onOpenChange={isOpen => {
              if (!isOpen) setOpenTooltip(null);
            }}
            placement="bottom"
          >
            <div
              onMouseEnter={() => handleTooltipMouseEnter('share')}
              onMouseLeave={handleTooltipMouseLeave}
              style={{ display: 'contents' }}
            >
              <ActionButton
                onPress={handleShareClick}
                aria-label="Share issue link"
                data-testid="slide-out-header-share-button"
                UNSAFE_className={styles.shareButton}
                isQuiet
              >
                <img
                  src={shareSvg}
                  alt=""
                  width={16}
                  height={16}
                  data-testid="slide-out-header-share-icon"
                />
              </ActionButton>
            </div>
            <Tooltip>Share Link</Tooltip>
          </TooltipTrigger>
        )}

        <div
          className={styles.chevronGroup}
          data-testid="slide-out-header-chevron-group"
        >
          <TooltipTrigger
            isOpen={openTooltip === 'chevron-left'}
            onOpenChange={isOpen => {
              if (!isOpen) setOpenTooltip(null);
            }}
            placement="bottom"
          >
            <div
              onMouseEnter={() => handleTooltipMouseEnter('chevron-left')}
              onMouseLeave={handleTooltipMouseLeave}
              style={{ display: 'contents' }}
            >
              <ActionButton
                onPress={onNavigatePrevious}
                isDisabled={!onNavigatePrevious}
                aria-label="Navigate to previous moment"
                data-testid="slide-out-header-chevron-left-container"
                UNSAFE_className={styles.chevronButton}
                isQuiet
              >
                <ChevronLeft
                  width={6}
                  height={6}
                  data-testid="slide-out-header-chevron-left"
                />
              </ActionButton>
            </div>
            <Tooltip>Previous RSD</Tooltip>
          </TooltipTrigger>
          <TooltipTrigger
            isOpen={openTooltip === 'chevron-right'}
            onOpenChange={isOpen => {
              if (!isOpen) setOpenTooltip(null);
            }}
            placement="bottom"
          >
            <div
              onMouseEnter={() => handleTooltipMouseEnter('chevron-right')}
              onMouseLeave={handleTooltipMouseLeave}
              style={{ display: 'contents' }}
            >
              <ActionButton
                onPress={onNavigateNext}
                isDisabled={!onNavigateNext}
                aria-label="Navigate to next moment"
                data-testid="slide-out-header-chevron-right-container"
                UNSAFE_className={styles.chevronButton}
                isQuiet
              >
                <ChevronRight
                  width={6}
                  height={6}
                  data-testid="slide-out-header-chevron-right"
                />
              </ActionButton>
            </div>
            <Tooltip>Next RSD</Tooltip>
          </TooltipTrigger>
        </div>
        {MaximizeIcon &&
          !isSmallScreen &&
          !(isMediumScreen && isCommentPanelOpen) && (
            <TooltipTrigger
              isOpen={openTooltip === 'maximize'}
              onOpenChange={isOpen => {
                if (!isOpen) setOpenTooltip(null);
              }}
              placement="bottom"
            >
              <div
                onMouseEnter={() => handleTooltipMouseEnter('maximize')}
                onMouseLeave={handleTooltipMouseLeave}
                style={{ display: 'contents' }}
              >
                <ActionButton
                  onPress={onMaximize}
                  aria-label={
                    isFullScreen ? 'Minimize panel' : 'Maximize panel'
                  }
                  data-testid="slide-out-header-maximize-button"
                  isQuiet
                >
                  <MaximizeIcon
                    width="16px"
                    height="16px"
                    flexShrink={0}
                    background="var(--Alias-content-neutral-default, #292929)"
                    data-testid="slide-out-header-maximize-icon"
                  />
                </ActionButton>
              </div>
              <Tooltip>{isFullScreen ? 'Minimize' : 'Maximize'}</Tooltip>
            </TooltipTrigger>
          )}
      </div>

      {/* Clone Modal */}
      <CloneMomentModal
        isOpen={isCloneModalOpen}
        onClose={() => setCloneModalOpen(false)}
        onClone={handleClone}
        currentMomentName={extractTitleOnly(title)}
        issueId={issueId}
        onCloneSuccess={handleCloneSuccess}
      />
    </View>
  );
};

export default Header;
